import { FunctionComponent } from 'react';
import { twMerge } from 'src/utils/twMerge';

import Button, {
  ButtonProps,
  disabledClasses,
} from 'src/designSystem/Button';
import CdnIcon from 'src/designSystem/CdnIcon';
import {
  ICON_BABZ_PILL,
  ICON_BLINGZ_PILL,
  ICON_BLIZZ_PILL,
} from 'src/components/StarRoad/utils/star-road-design-tokens';
import { useWallet } from 'src/queries/wallet';
import type { SpendableCurrency } from 'src/types/currency.types';

export type { SpendableCurrency };

export const CURRENCY_ICONS: Record<SpendableCurrency, string> = {
  BABZ: ICON_BABZ_PILL,
  BLINGZ: ICON_BLINGZ_PILL,
  BLIZZ: ICON_BLIZZ_PILL,
};

export const CURRENCY_BALANCES: Record<SpendableCurrency, 'babz' | 'blingz' | 'blizz'> = {
  BABZ: 'babz',
  BLINGZ: 'blingz',
  BLIZZ: 'blizz',
};

type Props = ButtonProps & {
  cost: number;
  currency?: SpendableCurrency;
  onTooPoorClick?: () => void;
  forceEnable?: boolean;
  pillClassName?: string;
};

const PriceButton: FunctionComponent<Props> = ({
  cost,
  currency = 'BABZ',
  onTooPoorClick,
  forceEnable = false,
  pillClassName,
  ...props
}) => {
  const babz = useWallet().data?.[CURRENCY_BALANCES[currency]];

  const isBalanceKnown = babz !== undefined;
  const hasEnoughBabz = isBalanceKnown && babz >= cost;

  // An unresolved wallet is unknown, not zero: do not route a solvent child to the too-poor panel.
  const isDisabled = forceEnable
    ? false
    : !hasEnoughBabz && (!isBalanceKnown || !onTooPoorClick);

  const isInert = (props.disabled || props.loading || isDisabled) ?? false;

  return (
    <div className="relative w-full">
      <Button
        {...props}
        className={twMerge(props.className, isInert && disabledClasses)}
        onClick={
          hasEnoughBabz || forceEnable
            ? props.onClick
            : isBalanceKnown
              ? onTooPoorClick
              : undefined
        }
        disabled={isInert}
      />
      <div
        className={
          'absolute right-1 top-0 flex items-center justify-center h-full px-3'
        }
      >
        <div
          className={twMerge(
            'flex flex-row items-center gap-1 p-1 bg-white/20 rounded-lg',
            !isInert && pillClassName,
          )}
        >
          <CdnIcon name={CURRENCY_ICONS[currency]} className="h-4 w-auto" />
          <span className="text-white font-bold text-xs">{cost}</span>
        </div>
      </div>
    </div>
  );
};

export default PriceButton;
